import moment from 'moment/moment'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import _ from 'lodash'
import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Popover } from 'reactstrap'
import { categoriesFilterOptions } from '../../../reducers/dashboardReducer'
import DatePicker from '../../common/form/DatePicker'
import Select from '../../common/form/Select'
import SubmitButton from '../../common/buttons/SubmitButton'

import styles from './FiltersButtonDropdown.module.scss'
import { ReactComponent as FilterIcon } from '../../../assets/images/common/icons/filter-icon.svg'

const useWidgetIds = (prefix) => useMemo(() => {
  const base = _.uniqueId(`${prefix}-`)
  return {
    buttonId: `${base}-button`,
    startDateId: `${base}-start-date`,
    endDateId: `${base}-end-date`,
    categoryId: `${base}-category`,
    programId: `${base}-program`,
  }
}, [prefix])

const currentDate = new Date()

export const FIXED_DATE_RANGES_FUNCTIONS = {
  '90days': {
    textFunc: () => 'Last 90 days',
    valueFunc: () => ({
      endDate: currentDate,
      startDate: moment().subtract(90, 'days')
        .toDate(),
    }),
  },
  '60days': {
    textFunc: () => 'Last 60 days',
    valueFunc: () => ({
      endDate: currentDate,
      startDate: moment().subtract(60, 'days')
        .toDate(),
    }),
  },
  '30days': {
    textFunc: () => 'Last 30 days',
    valueFunc: () => ({
      endDate: currentDate,
      startDate: moment().subtract(30, 'days')
        .toDate(),
    }),
  },
  '20days': {
    textFunc: () => 'Last 20 days',
    valueFunc: () => ({
      endDate: currentDate,
      startDate: moment().subtract(20, 'days')
        .toDate(),
    }),
  },
  thisYear: {
    textFunc: () => `This year (${moment().year()})`,
    valueFunc: () => ({
      startDate: moment().startOf('year')
        .toDate(),
      endDate: moment().endOf('year')
        .toDate(),
    }),
  },
  lastYear: {
    textFunc: () => `Last year (${moment().subtract(1, 'year').year()})`,
    valueFunc: () => ({
      startDate: moment().subtract(1, 'year').startOf('year')
        .toDate(),
      endDate: moment().subtract(1, 'year').endOf('year')
        .toDate(),
    }),
  },
}

const getTitleText = (startDate, endDate) => {
  const startDateMoment = moment(startDate)
  const endDateMoment = moment(endDate)
  const startThisYear = moment(currentDate).startOf('year')
  const endThisYear = moment(currentDate).endOf('year')
  const startLastYear = moment(currentDate).subtract(1, 'year').startOf('year')
  const endLastYear = moment(currentDate).subtract(1, 'year').endOf('year')

  if (startDateMoment.isSame(startThisYear, 'day') && endDateMoment.isSame(endThisYear, 'day')) {
    return `This Year (${currentDate.getFullYear()})`
  }

  if (startDateMoment.isSame(startLastYear, 'day') && endDateMoment.isSame(endLastYear, 'day')) {
    return `Last Year (${currentDate.getFullYear() - 1})`
  }

  if (endDateMoment.isSame(moment(currentDate), 'day')) {
    const ranges = { 20: 'Last 20 Days', 30: 'Last 30 Days', 31: 'Last 30 Days', 60: 'Last 60 Days', 90: 'Last 90 Days' }
    const diffDays = endDateMoment.diff(startDateMoment, 'days') + 1
    if (ranges[diffDays]) {
      return ranges[diffDays]
    }
  }

  return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
}

const FixedRangeList = ({ onSelectRange, className, buttonClassName }) => (
  <ul className={className}>
    {Object.keys(FIXED_DATE_RANGES_FUNCTIONS).map(key => (
      <li key={key}>
        <button className={buttonClassName} onClick={() => onSelectRange(key)}>
          {FIXED_DATE_RANGES_FUNCTIONS[key].textFunc()}
        </button>
      </li>
    ))}
  </ul>
)

FixedRangeList.propTypes = {
  onSelectRange: PropTypes.func.isRequired,
  className: PropTypes.string,
  buttonClassName: PropTypes.string,
}

const FiltersButtonDropdown = ({ title, defaultFilters, appliedFilters, onSubmitFilters, dateOnly }) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [editFilters, setEditFilters] = useState()

  const ids = useWidgetIds()

  const categoriesSelectOptions = useSelector(categoriesFilterOptions)
  const { programsFilterOptions } = useSelector(state => state.dashboard)

  const programsSelectOptions = editFilters?.categoryOption
    ? programsFilterOptions.filter(program => program.categoryId === editFilters.categoryOption.value)
    : programsFilterOptions

  const extraFiltersCount = dateOnly
    ? 0
    : (appliedFilters.categoryOption !== null ? 1 : 0) + (appliedFilters.programOption !== null ? 1 : 0)

  const handleToggle = () => {
    if (!isPopoverOpen) {
      setEditFilters(appliedFilters)
    }
    setIsPopoverOpen(prev => !prev)
  }

  const handleSetFixedRange = (range) => {
    const { startDate, endDate } = FIXED_DATE_RANGES_FUNCTIONS[range].valueFunc()
    setEditFilters(prev => ({ ...prev, startDate, endDate }))
  }

  const handleSetFilterValue = (filterName, value) => {
    setEditFilters(prev => ({ ...prev, [filterName]: value }))
  }

  const handleSetCategoryFilterValue = (categoryOption) => {
    const program = editFilters.programOption

    if (program !== null && categoryOption !== null && program.categoryId !== categoryOption.value) {
      setEditFilters(prev => ({ ...prev, programOption: null }))
    }

    setEditFilters(prev => ({ ...prev, categoryOption }))
  }

  const handleClear = () => {
    onSubmitFilters(defaultFilters)
    setIsPopoverOpen(false)
  }

  const handleSubmit = () => {
    onSubmitFilters(editFilters)
    setIsPopoverOpen(false)
  }

  return (
    <>
      <button
        id={ids.buttonId}
        className={styles.filtersButton}
        onClick={handleToggle}
      >
        <FilterIcon className={styles.filterIcon} />
        <span>{getTitleText(appliedFilters.startDate, appliedFilters.endDate)}</span>
        {extraFiltersCount > 0 && (
          <span className={styles.extraFilter}>+ {extraFiltersCount} filter(s)</span>
        )}
      </button>

      <Popover
        target={ids.buttonId}
        isOpen={isPopoverOpen}
        placement='bottom-end'
        trigger='legacy'
        toggle={handleToggle}
        popperClassName={classnames(styles.filtersMenu, { [styles.dateOnlyMenu]: dateOnly })}
      >
        <div className={classnames(styles.filtersBody, { [styles.dateOnlyBody]: dateOnly })}>
          {!dateOnly && (
            <div className={styles.fixedRangeSidebar}>
              <span className={styles.sidebarTitle}>Fixed Range</span>
              <FixedRangeList
                onSelectRange={handleSetFixedRange}
                className={styles.rangeList}
                buttonClassName={styles.rangeButton}
              />
            </div>
          )}

          <div className={styles.filtersPanel}>
            <h5 className={styles.panelTitle}>{title ?? 'Filter Options'}</h5>

            <div className={styles.datePickersRow}>
              <div className={styles.datePickerField}>
                <label className={styles.fieldLabel} htmlFor={ids.startDateId}>Start Date</label>
                <DatePicker
                  id={ids.startDateId}
                  className={styles.datePicker}
                  selected={editFilters?.startDate}
                  onChange={date => handleSetFilterValue('startDate', date)}
                  dateFormat='MMMM, d eee'
                  symbol={<i className={classnames(styles.calendarIcon, 'fa fa-calendar')} />}
                />
              </div>
              <div className={styles.datePickerField}>
                <label className={styles.fieldLabel} htmlFor={ids.endDateId}>End Date</label>
                <DatePicker
                  id={ids.endDateId}
                  className={styles.datePicker}
                  selected={editFilters?.endDate}
                  onChange={date => handleSetFilterValue('endDate', date)}
                  dateFormat='MMMM, d eee'
                  symbol={<i className={classnames(styles.calendarIcon, 'fa fa-calendar')} />}
                />
              </div>
            </div>

            {dateOnly && (
              <FixedRangeList
                onSelectRange={handleSetFixedRange}
                className={styles.inlineRangeList}
                buttonClassName={styles.inlineRangeButton}
              />
            )}

            {!dateOnly && (
              <>
                <div className={styles.selectField}>
                  <label className={styles.fieldLabel} htmlFor={ids.categoryId}>Program Category</label>
                  <Select
                    id={ids.categoryId}
                    placeholder='Choose a Program Category'
                    options={categoriesSelectOptions}
                    value={editFilters?.categoryOption}
                    classNames={{ menuList: () => styles.selectMenuList }}
                    onChange={option => handleSetCategoryFilterValue(option)}
                    isClearable
                  />
                </div>
                <div className={classnames(styles.selectField, styles.selectFieldSpaced)}>
                  <label className={styles.fieldLabel} htmlFor={ids.programId}>Program Title</label>
                  <Select
                    id={ids.programId}
                    isSearchable
                    placeholder='Choose a Program Title'
                    options={programsSelectOptions}
                    value={editFilters?.programOption}
                    classNames={{ menuList: () => styles.selectMenuList }}
                    onChange={option => handleSetFilterValue('programOption', option)}
                    isClearable
                  />
                </div>
              </>
            )}

            <div className={styles.actionButtons}>
              <button className={styles.clearButton} onClick={handleClear}>
                Clear <i className='ml-4 fa fa-close' />
              </button>
              <SubmitButton
                buttonMessage='Apply'
                className={styles.applyButton}
                onClick={handleSubmit}
              />
            </div>
          </div>
        </div>
      </Popover>
    </>
  )
}

FiltersButtonDropdown.propTypes = {
  title: PropTypes.string,
  defaultFilters: PropTypes.shape({
    startDate: PropTypes.instanceOf(Date).isRequired,
    endDate: PropTypes.instanceOf(Date).isRequired,
    categoryOption: PropTypes.object,
    programOption: PropTypes.object,
  }).isRequired,
  appliedFilters: PropTypes.shape({
    startDate: PropTypes.instanceOf(Date).isRequired,
    endDate: PropTypes.instanceOf(Date).isRequired,
    categoryOption: PropTypes.object,
    programOption: PropTypes.object,
  }).isRequired,
  onSubmitFilters: PropTypes.func.isRequired,
  dateOnly: PropTypes.bool,
}

export default FiltersButtonDropdown
